/**
 * **The attack forecast, as one viewer reads it** (`projects/target-hover-forecast.md` 2.1 and 3.2):
 * forecast one attack, mask it for the viewer, find the pre-roll choice it cannot see through, and
 * turn it into display rows. The browser's hover panel (`TargetForecastPanel.tsx`) and the CLI's
 * `forecast-attack` (`cli/src/attackForecastView.ts`) both read these rows, so the two surfaces word
 * a forecast the same way.
 *
 * The contribution bits use the receipt's own grammar (`displaySourceName`, `+1`/`−1`, `via <ally>`),
 * so the net line reads the same as the committed attack's `TO HIT` provenance row.
 *
 * Pure: sends nothing, draws no dice.
 */

import { maskAttackForecast, type ActorRole, type AttackForecastContribution, type AttackForecastCost, type EncounterState, type MaskedAttackForecast } from "@lancer-vtt/domain";
import { forecastAttack, type ForecastAttackArgs } from "./attackForecast";
import type { LoadedOverlay } from "./effects";
import { displaySourceName } from "./narrate";
import { previewAttackDeclaredChoice } from "./resolve/preview";

/** Who the forecast is masked for: a session's own login, or a player a GM is previewing. */
export type ForecastViewer = { role: ActorRole; viewerSlot: string | null };

/** A pre-roll `choose` the forecast cannot see through, because nothing preselected it. */
export type ForecastDependsOn = { promptId: string; prompt: string; options: string[] };

export type AttackForecastView = { ok: true; forecast: MaskedAttackForecast; dependsOn?: ForecastDependsOn } | { ok: false; illegal: string };

/** Forecast and mask one attack for `viewer`, with the declared-phase choice it depends on. */
export function viewAttackForecast(args: {
  state: EncounterState;
  overlay: LoadedOverlay;
  attack: Omit<ForecastAttackArgs, "overlay">;
  viewer: ForecastViewer;
}): AttackForecastView {
  const { state, overlay, attack, viewer } = args;
  const raw = forecastAttack(state, { ...attack, overlay });
  if ("illegal" in raw) return { ok: false, illegal: raw.illegal };
  const forecast = maskAttackForecast(raw, { state, viewerSlot: viewer.viewerSlot, role: viewer.role });
  // The same declared-phase probe the server runs for the `preRollChoices` sidecar.
  const prompt = attack.tech ? undefined : previewAttackDeclaredChoice({ state, attackerId: attack.attackerId, weapon: attack.weapon, overlay });
  const dependsOn =
    prompt !== undefined && attack.choices?.[prompt.promptId] === undefined
      ? { promptId: prompt.promptId, prompt: prompt.prompt, options: prompt.options.map((o) => o.id) }
      : undefined;
  return { ok: true, forecast, ...(dependsOn !== undefined ? { dependsOn } : {}) };
}

/** What a masked number prints as. */
export const FORECAST_UNKNOWN_TEXT = "??";

/** `+1` / `−1` with a real minus sign, as the receipt prints a contribution. */
export function signedTight(n: number): string {
  return n >= 0 ? `+${n}` : `−${Math.abs(n)}`;
}

function pct(p: number): string {
  return `${Math.round(p * 100)}%`;
}

/** One contribution, named for display. `via` is the conferring ally's name when neither side carries it. */
export type ForecastContributionRow = { kind: AttackForecastContribution["kind"]; name: string; value: number; valueText: string; via?: string };

/** A masked forecast as display rows. Every string is final; a surface only lays them out. */
export type AttackForecastRows = {
  /** `+1 ACC`, `−2 DIFF` or `STRAIGHT ROLL`. */
  netLabel: string;
  net: number;
  /**
   * What the card leads with: the hit chance (`63% HIT`) with the net as a small tag beside it, or,
   * when the defence is masked and there is no hit chance, the net itself with no tag.
   */
  headline: { text: string; tag?: string };
  /** The to-hit roll as the dice run: `1d20 +1 −best of 2d6`. The accuracy term is omitted at a net of 0. */
  rollText: string;
  contributions: ForecastContributionRow[];
  /** The to-hit bonus, signed (`+3`). */
  bonusText: string;
  defence: {
    vs: "evasion" | "edef";
    /** `EVASION` or `E-DEF`. */
    label: string;
    /** The number, or `??`. */
    valueText: string;
    /** Stunned's cap: `capped from 12 at 5 — Stunned`, or `max 5 — Stunned` when the uncapped value is masked. */
    capNote?: string;
  };
  /** `??` when the defence is masked: a Scan would let the viewer compute it. */
  hitChance: { hit: string; crit: string };
  /** The damage modifiers, one bit each (`ARMOR 2: −2 damage`, `RESIST energy`, `EXPOSED ×2`), empty when none apply. `null` for an attack that deals no weapon damage. */
  damage: string[] | null;
  /** Pre-roll riders: Invisible's coin, advantage, what a hit consumes, what the attack's charges cost. */
  riders: string[];
  warnings: string[];
};

/**
 * The forecast card's rule-stating copy, as data for `frontend/src/rulesCopyRegistry.ts`. The roll
 * term is Core p.13: each point of Accuracy adds 1d6 and each point of Difficulty subtracts one, and
 * several dice are never summed, the highest is applied. The Armor line is Core p.66 (Armor reduces
 * all incoming damage by its rating, and AP weapons and burn ignore it), said as what it does to this hit.
 */
export const FORECAST_COPY = {
  /** The headline when the hit chance is known: `63% HIT`. */
  hitHeadline: (hit: string) => `${hit} HIT`,
  /** The accuracy pool as the dice run it: `+1d6`, `−best of 2d6`. */
  accuracyDice: (net: number) => {
    const n = Math.abs(net);
    return `${net > 0 ? "+" : "−"}${n === 1 ? "1d6" : `best of ${n}d6`}`;
  },
  /** Armor the hit's damage meets: `ARMOR 2: −2 damage`, or `−2 kinetic damage` when some of its types skip Armor. */
  armorReduces: (armor: number, types?: string[]) => `ARMOR ${armor}: −${armor}${types ? ` ${types.join(", ")}` : ""} damage`,
  /** Every damage type this weapon deals skips Armor (burn, heat). */
  armorSkipped: (armor: number, types: string[]) => `ARMOR ${armor}: no effect on ${types.join(", ")}`,
  apIgnores: (armor: number | undefined) => (armor === undefined ? "AP: ignores ARMOR" : `AP: ignores ARMOR ${armor}`),
  armorUnknown: `ARMOR ${FORECAST_UNKNOWN_TEXT}`,
  shredded: "SHREDDED: ignores ARMOR",
  /** A charge cost (Ammo Case): `expends 2 charges of Ammo Case III, waived on a crit (Efficiency)`. */
  cost: (c: AttackForecastCost) => `expends ${c.amount} charge${c.amount === 1 ? "" : "s"} of ${c.poolName}${c.waivedOnCrit !== undefined ? `, waived on a crit (${c.waivedOnCrit})` : ""}`,
} as const;

/** The Armor line: what the target's Armor does to this hit's damage, or `undefined` when it has none. */
function armorLine(d: NonNullable<MaskedAttackForecast["damage"]>): string | undefined {
  if (d.armorIgnored) return FORECAST_COPY.shredded;
  if (d.armor === "unknown") return d.ap ? FORECAST_COPY.apIgnores(undefined) : FORECAST_COPY.armorUnknown;
  if (d.armor <= 0) return undefined;
  if (d.ap) return FORECAST_COPY.apIgnores(d.armor);
  const perType = d.perType;
  if (perType === "unknown") return FORECAST_COPY.armorReduces(d.armor);
  const types = (armored: boolean) => [...new Set(perType.filter((p) => (p.armor > 0) === armored).map((p) => p.type))];
  const skipped = types(false);
  if (skipped.length === 0) return FORECAST_COPY.armorReduces(d.armor);
  const armored = types(true);
  return armored.length === 0 ? FORECAST_COPY.armorSkipped(d.armor, skipped) : FORECAST_COPY.armorReduces(d.armor, armored);
}

/** Turn a masked forecast into display rows. `state` names a conferring ally. */
export function attackForecastRows(state: EncounterState, f: MaskedAttackForecast): AttackForecastRows {
  const nameOf = (id: string): string => state.combatants[id]?.name ?? id;
  const netLabel = f.net > 0 ? `+${f.net} ACC` : f.net < 0 ? `−${Math.abs(f.net)} DIFF` : "STRAIGHT ROLL";
  const contributions = f.contributions.map(({ kind, name: raw, carrier, value }): ForecastContributionRow => {
    const conferred = carrier !== f.attackerId && carrier !== f.targetId;
    const name = kind === "intent" || kind === "cover" ? raw : displaySourceName(raw);
    return { kind, name, value, valueText: signedTight(value), ...(conferred ? { via: nameOf(carrier) } : {}) };
  });

  const defence: AttackForecastRows["defence"] = {
    vs: f.defence.vs,
    label: f.defence.vs === "edef" ? "E-DEF" : "EVASION",
    valueText: f.defence.value === "unknown" ? FORECAST_UNKNOWN_TEXT : String(f.defence.value),
  };
  if (f.defence.evasionCap !== undefined && f.defence.vs === "evasion") {
    defence.capNote = f.defence.cappedFrom !== undefined ? `capped from ${f.defence.cappedFrom} at ${f.defence.evasionCap} — Stunned` : `max ${f.defence.evasionCap} — Stunned`;
  }

  const hitChance =
    f.hitChance === "unknown" ? { hit: FORECAST_UNKNOWN_TEXT, crit: FORECAST_UNKNOWN_TEXT } : { hit: pct(f.hitChance.hit), crit: pct(f.hitChance.crit) };

  let damage: string[] | null = null;
  const d = f.damage;
  if (d !== null) {
    const armor = armorLine(d);
    damage = armor === undefined ? [] : [armor];
    if (d.perType === "unknown") {
      damage.push(`RESIST ${FORECAST_UNKNOWN_TEXT}  IMMUNE ${FORECAST_UNKNOWN_TEXT}`);
    } else {
      const resisted = [...new Set(d.perType.filter((p) => p.resistant).map((p) => p.type))];
      const immune = [...new Set(d.perType.filter((p) => p.immune).map((p) => p.type))];
      if (resisted.length > 0) damage.push(`RESIST ${resisted.join(", ")}`);
      if (immune.length > 0) damage.push(`IMMUNE ${immune.join(", ")}`);
    }
    if (d.doubled) damage.push("EXPOSED ×2");
    if (d.substituteStructure) damage.push("a hit deals 1 structure instead");
    if (d.overshield === "unknown") damage.push(`OVERSHIELD ${FORECAST_UNKNOWN_TEXT}`);
    else if (d.overshield > 0) damage.push(`OVERSHIELD ${d.overshield}`);
  }

  const riders: string[] = [];
  if (f.riders.missChance !== undefined) riders.push(`Invisible: ${pct(f.riders.missChance)} miss chance before the roll`);
  if (f.riders.advantage) riders.push("advantage: the d20 rolls twice, keep the higher");
  if (f.riders.consumeOnHit.length > 0) riders.push(`may consume ${f.riders.consumeOnHit.join(", ")} for its +1 (you are asked before the roll)`);
  for (const c of f.riders.costs ?? []) riders.push(FORECAST_COPY.cost(c));

  const headline = f.hitChance === "unknown" ? { text: netLabel } : { text: FORECAST_COPY.hitHeadline(hitChance.hit), tag: netLabel };
  const rollText = ["1d20", ...(f.bonus !== 0 ? [signedTight(f.bonus)] : []), ...(f.net !== 0 ? [FORECAST_COPY.accuracyDice(f.net)] : [])].join(" ");

  return { netLabel, net: f.net, headline, rollText, contributions, bonusText: signedTight(f.bonus), defence, hitChance, damage, riders, warnings: f.warnings };
}

/** The badge a legal target carries while an attack is armed (`projects/target-mini-forecasts.md` 1.1): the net roll and the hit chance, nothing else. */
export type MiniForecast = {
  /** `+1 ACC`, `−2 DIFF` or `STRAIGHT`. Known even when the defence is masked. */
  net: string;
  /** `62%`, or `??` when the viewer's mask hides the defence (a Scan would reveal it). */
  hit: string;
};

/** Shrink a forecast's display rows to its badge. The strings are the rows' own, so the badge and the full card cannot disagree. */
export function miniForecast(rows: AttackForecastRows): MiniForecast {
  return { net: rows.net === 0 ? "STRAIGHT" : rows.netLabel, hit: rows.hitChance.hit };
}
